<template>
    <div class="activix-scroll" @mouseenter="isHovering = true" @mouseleave="isHovering = false">
        <div class="activix-scroll-viewport" ref="scrollContainer" @scroll="onScroll">
            <slot />
        </div>
        <div class="activix-scroll-track" v-if="hasOverflow">
            <div
                class="activix-scroll-thumb"
                :class="{ 'is-visible': isHovering || isScrolling }"
                :style="thumbStyle"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    /**
     * Scrollable container with a custom overlay scrollbar that fades in/out.
     * Replacement for vuescroll (Vue 2). Wraps slotted content in a
     * vertical-only overflow container and exposes a `scrollTo` method
     * for programmatic scrolling.
     *
     * The scrollbar thumb fades in on hover or while actively scrolling,
     * and fades out after 0.6 s of inactivity.
     */
    import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

    defineOptions({ name: 'ActivixScroll' });

    const emit = defineEmits<{
        'handle-scroll': [
            payload: {
                scrollTop: number;
                scrollLeft: number;
                clientHeight: number;
                scrollHeight: number;
            }
        ];
    }>();

    const scrollContainer = ref<HTMLElement | null>(null);
    const isHovering = ref(false);
    const isScrolling = ref(false);
    const scrollTop = ref(0);
    const scrollHeight = ref(0);
    const clientHeight = ref(0);
    const SCROLL_TIMEOUT = 600; // [ms]

    let scrollTimeout: ReturnType<typeof setTimeout> | null = null;
    let resizeObserver: ResizeObserver | null = null;

    const hasOverflow = computed(() => scrollHeight.value > clientHeight.value);

    const thumbHeight = computed(() => {
        if (!hasOverflow.value) {
            return 0;
        }

        const ratio = clientHeight.value / scrollHeight.value;

        return Math.max(ratio * clientHeight.value, 24);
    });

    const thumbTop = computed(() => {
        if (!hasOverflow.value) {
            return 0;
        }

        const maxScroll = scrollHeight.value - clientHeight.value;
        const maxThumbTop = clientHeight.value - thumbHeight.value;

        return maxScroll > 0 ? (scrollTop.value / maxScroll) * maxThumbTop : 0;
    });

    const thumbStyle = computed(() => ({
        height: `${thumbHeight.value}px`,
        transform: `translateY(${thumbTop.value}px)`,
    }));

    const updateDimensions = () => {
        const el = scrollContainer.value;

        if (!el) {
            return;
        }

        scrollHeight.value = el.scrollHeight;
        clientHeight.value = el.clientHeight;
    };

    const onScroll = (event: Event) => {
        const target = event.target as HTMLElement;

        scrollTop.value = target.scrollTop;
        scrollHeight.value = target.scrollHeight;
        clientHeight.value = target.clientHeight;
        isScrolling.value = true;

        if (scrollTimeout) {
            clearTimeout(scrollTimeout);
        }

        scrollTimeout = setTimeout(() => {
            isScrolling.value = false;
        }, SCROLL_TIMEOUT);

        emit('handle-scroll', {
            scrollTop: target.scrollTop,
            scrollLeft: target.scrollLeft,
            clientHeight: target.clientHeight,
            scrollHeight: target.scrollHeight,
        });
    };

    const scrollTo = (
        { y = 0, x = 0 }: { y?: number; x?: number },
        { smooth = false }: { smooth?: boolean } = {}
    ) => {
        scrollContainer.value?.scrollTo({
            top: y,
            left: x,
            behavior: smooth ? 'smooth' : 'auto',
        });
    };

    onMounted(() => {
        updateDimensions();

        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(updateDimensions);

            if (scrollContainer.value) {
                resizeObserver.observe(scrollContainer.value);
            }
        }
    });

    onBeforeUnmount(() => {
        if (scrollTimeout) {
            clearTimeout(scrollTimeout);
        }

        resizeObserver?.disconnect();
    });

    defineExpose({ scrollTo });
</script>

<style lang="scss" scoped>
    .activix-scroll {
        position: relative;
        flex: 1 1 0%;
        min-height: 0;
        overflow: hidden;
    }

    .activix-scroll-viewport {
        height: 100%;
        overflow-y: auto;
        overflow-x: hidden;
        overscroll-behavior: contain;

        // Hide native scrollbar
        scrollbar-width: none; // Firefox
        -ms-overflow-style: none; // IE/Edge legacy

        &::-webkit-scrollbar {
            display: none; // Webkit
        }
    }

    .activix-scroll-track {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        width: 8px;
        pointer-events: none;
    }

    .activix-scroll-thumb {
        width: 100%;
        border-radius: 3px;
        background: rgba(0, 0, 0, 0.4);
        opacity: 0;
        transition: opacity 0.3s ease;
        will-change: transform;

        &.is-visible {
            opacity: 1;
        }
    }
</style>
